import clsx from 'clsx';
import { ArrowLeft } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';

import { PortalLanguageToggle } from './PortalLanguageToggle';
import { PORTAL_NAV_ITEMS } from './portalNavigation';
import { PortalStarsMenu } from './PortalStarsMenu';
import { getPortalUpDestination } from './portalUpNavigation';

/** English source titles; the header translates them at render time. */
export const ROUTE_TITLES = [
  { path: '/portal/checkout/*', title: 'Checkout' },
  { path: '/portal/courses/:courseSlug', title: 'Course details' },
  { path: '/portal/tutoring/:slug', title: 'Tutor profile' },
  { path: '/portal/academy/products/:slug', title: 'Exam details' },
  { path: '/portal/academy/checkout/:sku', title: 'Exam checkout' },
  { path: '/portal/academy/orders/:intentId', title: 'Exam order' },
  { path: '/portal/academy/reports/:sessionId', title: 'Exam report' },
  { path: '/portal/challenge/:slug/register', title: 'Register' },
  { path: '/portal/challenge/:slug/report', title: 'Report' },
  { path: '/portal/challenge/:slug', title: 'Code challenge' },
  { path: '/portal/wallet/topup', title: 'Top up Stars' },
  { path: '/portal/wallet/auto-topup', title: 'Auto top-up' },
  { path: '/portal/wallet/stars-guide', title: 'How Stars work' },
  { path: '/portal/wallet', title: 'Wallet and limits' },
  { path: '/portal/usage/*', title: 'Star usage' },
  { path: '/portal/usage', title: 'Star usage' },
  { path: '/portal/billing', title: 'Billing' },
  { path: '/portal/family/new', title: 'Add a kid' },
  { path: '/portal/family/:kidId/projects', title: 'Kid projects' },
  { path: '/portal/family/:kidId/passport', title: 'Passport' },
  { path: '/portal/family/:kidId/images', title: 'Kid pictures' },
  { path: '/portal/family/:kidId/settings', title: 'Kid settings' },
  { path: '/portal/family/:kidId', title: 'Activities' },
  { path: '/portal/audit/project/:id', title: 'Project activity' },
] as const;

function getPortalTitle(pathname: string) {
  const specificTitle = ROUTE_TITLES.find(({ path }) => matchPath({ path, end: true }, pathname));
  if (specificTitle) return specificTitle.title;

  const navItem = [...PORTAL_NAV_ITEMS]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => matchPath({ path: item.to, end: item.end ?? false }, pathname));

  return navItem?.label ?? 'Parent Portal';
}

interface PortalHistoryEntry {
  key: string;
  pathname: string;
  search: string;
  hash: string;
}

function portalHistoryLabel(pathname: string): string {
  if (pathname === '/portal' || pathname === '/portal/') return 'Home';
  return getPortalTitle(pathname);
}

/**
 * Tracks only routes visited while this Portal layout stays mounted. This lets
 * the header return to the actual entry screen without trusting browser history
 * that may lead to another surface or an external site.
 */
function usePortalReturnEntry(): PortalHistoryEntry | null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const stackRef = useRef<PortalHistoryEntry[]>([]);
  const [returnEntry, setReturnEntry] = useState<PortalHistoryEntry | null>(null);

  useLayoutEffect(() => {
    const entry: PortalHistoryEntry = {
      key: location.key,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
    let nextStack = stackRef.current;
    const existingIndex = nextStack.findIndex((item) => item.key === entry.key);

    if (navigationType === 'POP' && existingIndex >= 0) {
      nextStack = nextStack.slice(0, existingIndex + 1);
    } else if (navigationType === 'REPLACE' && nextStack.length > 0) {
      nextStack = [...nextStack.slice(0, -1), entry];
    } else if (nextStack.at(-1)?.key !== entry.key) {
      nextStack = [...nextStack, entry];
    }

    stackRef.current = nextStack;
    const nextReturnEntry = nextStack.length > 1 ? (nextStack.at(-2) ?? null) : null;
    setReturnEntry((current) =>
      current?.key === nextReturnEntry?.key ? current : nextReturnEntry,
    );
  }, [location.hash, location.key, location.pathname, location.search, navigationType]);

  return returnEntry;
}

interface PortalMobileHeaderProps {
  onStarsFullscreenOpenChange?: (isOpen: boolean) => void;
}

export function PortalMobileHeader({ onStarsFullscreenOpenChange }: PortalMobileHeaderProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const returnEntry = usePortalReturnEntry();
  const upDestination = getPortalUpDestination(pathname, search);
  const { t } = usePortalLocale();
  const backLabel = returnEntry
    ? t('Back to {label}', { label: t(portalHistoryLabel(returnEntry.pathname)) })
    : '';
  const upLabel = upDestination ? t('Up to {label}', { label: t(upDestination.label) }) : '';
  const navigationClassName = clsx(
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-hairline bg-canvas-pure text-ink transition-colors hover:border-ink/25 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral',
  );

  return (
    <header
      className="sticky top-3 z-50 mx-3 mt-3 flex h-14 max-w-[1440px] items-center gap-3 rounded-full border border-ink/15 bg-canvas-pure px-3 shadow-floating-chrome sm:mx-6 sm:px-5 lg:mx-auto xl:h-16 xl:px-6"
      data-testid="portal-mobile-header"
    >
      {upDestination &&
        (returnEntry ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={backLabel}
            title={backLabel}
            className={navigationClassName}
            data-testid="portal-up-navigation"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
          </button>
        ) : (
          <Link
            to={upDestination.to}
            replace
            aria-label={upLabel}
            title={upLabel}
            className={navigationClassName}
            data-testid="portal-up-navigation"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
          </Link>
        ))}
      <Link
        to="/portal"
        aria-label={t('Airbotix home')}
        title={t('Go to Parent Portal Home')}
        className={clsx(
          '-m-1 shrink-0 rounded-lg p-1 transition-colors hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral',
          upDestination && 'hidden sm:block',
        )}
      >
        <img src="/logo-black-horizontal-approved-20261021.png" alt="Airbotix" className="h-7 w-auto xl:h-8" />
      </Link>
      <span className="h-8 w-px shrink-0 bg-hairline" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[9px] font-bold uppercase leading-none tracking-[0.11em] text-slate2">
          {t('Parent Portal')}
        </p>
        <p
          className="mt-0.5 truncate text-[15px] font-bold leading-5 text-ink"
          data-testid="portal-route-title"
        >
          {t(getPortalTitle(pathname))}
        </p>
      </div>
      <PortalStarsMenu onFullscreenOpenChange={onStarsFullscreenOpenChange} />
      <PortalLanguageToggle />
    </header>
  );
}
